import type { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import type { campaigns, triggers } from '@bb/contracts';
import { Button, ConfirmDialog } from '@bb/ui';
import { ApiError, type AdminApi, type CampaignDraftIssue, type CampaignDraftValidation, type CampaignView } from '../../../api/client.ts';
import { formatCount } from '../../audience/ChannelReachBreakdown.tsx';
import { CHANNEL_LABELS } from '../compose/messagesDraft.ts';
import { DELIVERY_LABELS } from '../labels.ts';
import { IssueGroups } from './StepTracker.tsx';
import { fromServer, type CampaignWizardDraft, type WizardIssue, type WizardStepId } from './steps.ts';

const UNIT: Record<string, string> = { day: 'day', week: 'week', month: 'month' };

/** When a scheduled campaign sends, in words. */
export function describeSchedule(s: campaigns.ScheduledDelivery | undefined): string {
  if (!s) return 'Not chosen yet';
  if (s.kind === 'immediate') return 'As soon as it is launched';
  const zone = s.localTime ? ' in each user’s local time' : '';
  if (s.kind === 'at') return `Once on ${s.date} at ${s.time}${zone}`;
  const every = s.interval > 1 ? `Every ${s.interval} ${UNIT[s.every]}s` : `Every ${UNIT[s.every]}`;
  return `${every} at ${s.time}${zone}, from ${s.startDate}${s.endDate ? ` to ${s.endDate}` : ''}`;
}

const TRIGGER_WORDS: Record<triggers.TriggerSpec['kind'], string> = {
  custom_event: 'custom event',
  session_start: 'session start',
  attribute_changed: 'attribute change',
  subscription_changed: 'subscription change',
  message_interaction: 'message interaction',
} as Record<triggers.TriggerSpec['kind'], string>;

/** What triggers an action-based campaign, in words. */
export function describeTriggers(a: triggers.ActionBasedDelivery | undefined): string {
  if (!a?.triggers.length) return 'Not chosen yet';
  return a.triggers.map((t) => (t.kind === 'custom_event' ? `«${t.event || '…'}»` : (TRIGGER_WORDS[t.kind] ?? t.kind))).join(' or ');
}

export interface ReviewStepProps {
  api: AdminApi;
  environmentId: string;
  campaign: CampaignView;
  draft: CampaignWizardDraft;
  validation: UseQueryResult<CampaignDraftValidation>;
  /** Everything the wizard shows: the server's check and the client's checks of unsaved parts. */
  issues: WizardIssue[];
  dirty: boolean;
  canPublish: boolean;
  readOnly: boolean;
  /** Saves pending edits; rejects with a message when something could not be saved. */
  onSave: () => Promise<void>;
  onGoTo: (step: WizardStepId) => void;
  /** The campaign as publish returned it. */
  onLaunched: (campaign: CampaignView) => void;
}

const reachText = (reach: CampaignDraftValidation['reach']) =>
  reach ? `about ${formatCount(reach.count)} users (${reach.mode === 'exact' ? 'exact' : `${formatCount(reach.low)}–${formatCount(reach.high)}`}); reachable by push: Android ${formatCount(reach.reachable.push_android ?? 0)}, iOS ${formatCount(reach.reachable.push_ios ?? 0)}` : 'not known';

/**
 * Review (BB-97): a summary of every step, every problem by step with a way to it, and Launch / Update. Launch saves
 * pending edits, checks the stored draft once more, asks for confirmation with the reach and publishes; a refused
 * publish (422) shows the server's issues.
 */
export function ReviewStep({ api, environmentId, campaign, draft, validation, issues, dirty, canPublish, readOnly, onSave, onGoTo, onLaunched }: ReviewStepProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [launchIssues, setLaunchIssues] = useState<WizardIssue[] | null>(null);
  const [confirm, setConfirm] = useState<CampaignDraftValidation | null>(null);
  const live = campaign.published !== null;
  const errors = issues.filter((i) => i.severity === 'error');
  const messages = draft.content.messages;
  const { schedule, deliveryControls: controls, conversions } = draft.content;
  const reach = validation.data?.reach ?? null;
  const nothingToPublish = live && !dirty && !campaign.hasDraftChanges;
  const blocked =
    campaign.status === 'archived'
      ? 'The campaign is archived: restore it first.'
      : campaign.status === 'completed'
        ? 'The campaign has completed. Duplicate it to send it again.'
        : !canPublish
          ? 'Launching needs the messages.publish permission: ask a publisher to launch it.'
          : null;
  const action = live ? 'Update campaign' : 'Launch campaign';

  const launch = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    setLaunchIssues(null);
    try {
      if (dirty) await onSave();
      // Publish takes the stored draft: check it once more.
      const check = await api.validateCampaignDraft(environmentId, campaign.id);
      if (!check.valid) return setLaunchIssues(check.issues.filter((i) => i.severity === 'error').map(fromServer));
      setConfirm(check);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const view = await api.campaignAction(environmentId, campaign.id, 'publish');
      setConfirm(null);
      onLaunched(view);
      setResult(live ? `Updated: revision ${view.publishedRevision ?? ''} is running.` : `Launched: the campaign is ${view.status}.`);
    } catch (e) {
      setConfirm(null);
      const refused = e instanceof ApiError ? (e.problem.issues as CampaignDraftIssue[] | undefined) : undefined;
      if (refused?.length) setLaunchIssues(refused.filter((i) => i.severity === 'error').map(fromServer));
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Review" className="canvas-wizard__panel">
      <h2>Review</h2>
      <section aria-label="Problems">
        <h3>Problems</h3>
        {issues.length === 0 ? <p>No problems: the campaign is ready to launch.</p> : <IssueGroups issues={issues} onGoTo={onGoTo} label="Problems by step" />}
      </section>

      <section aria-label="Summary">
        <h3>Summary</h3>
        <dl className="facts">
          <dt>Name</dt>
          <dd>{draft.name || '—'}</dd>
          {draft.description && (
            <>
              <dt>Goal</dt>
              <dd>{draft.description}</dd>
            </>
          )}
          <dt>Channels</dt>
          <dd>{Array.isArray(messages) || messages.channels.length === 0 ? 'No message yet' : messages.channels.map((c) => CHANNEL_LABELS[c]).join(', ')}</dd>
          {!Array.isArray(messages) && (
            <>
              <dt>Variants</dt>
              <dd>
                {messages.variants.map((v) => `${v.name} ${v.percent}%`).join(', ')}
                {messages.control.percent > 0 ? `, control ${messages.control.percent}%` : ', no control group'}
              </dd>
            </>
          )}
          <dt>Delivery</dt>
          <dd>
            {DELIVERY_LABELS[campaign.deliveryType]}:{' '}
            {campaign.deliveryType === 'scheduled' ? describeSchedule(schedule.scheduled) : campaign.deliveryType === 'action_based' ? `when a user does ${describeTriggers(schedule.actionBased)}` : 'when your server calls the trigger API'}
          </dd>
          <dt>Delivery controls</dt>
          <dd>
            {[
              controls.quietHours?.enabled ? `quiet hours ${controls.quietHours.from}–${controls.quietHours.to}` : 'no quiet hours',
              controls.ratePerMinute ? `up to ${formatCount(controls.ratePerMinute)} sends a minute` : 'no rate limit of its own',
              controls.reeligibility ? 'users may receive it again' : 'each user receives it once',
            ].join('; ')}
          </dd>
          <dt>Audience</dt>
          <dd>{draft.content.audience ? 'Users who match the audience' : 'All users'}</dd>
          <dt>Reach</dt>
          <dd>{validation.isFetching && !reach ? 'Estimating…' : reachText(reach)}</dd>
          <dt>Conversion events</dt>
          <dd>{conversions.length === 0 ? 'Default: a session start within 3 days' : conversions.map((c, i) => `${c.kind === 'custom_event' ? `«${c.event || '…'}»` : 'session start'}${i === 0 ? ' (primary)' : ''}`).join(', ')}</dd>
        </dl>
      </section>

      <section aria-label="Launch" className="canvas-wizard__launch">
        {blocked && <p className="hint">{blocked}</p>}
        {!blocked && nothingToPublish && <p className="hint">No unpublished changes: the running version is up to date.</p>}
        {!blocked && errors.length > 0 && <p className="hint">Fix the problems above to launch.</p>}
        {launchIssues && launchIssues.length > 0 && <IssueGroups issues={launchIssues} onGoTo={onGoTo} label="Launch refused" />}
        {error && <p role="alert">{error}</p>}
        {result && <p role="status">{result}</p>}
        {!blocked && !readOnly && (
          <Button variant="primary" loading={busy} disabled={errors.length > 0 || nothingToPublish} onClick={() => void launch()}>
            {action}
          </Button>
        )}
      </section>

      <ConfirmDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)} title={`${live ? 'Update' : 'Launch'} “${draft.name}”?`} confirmLabel={action} busy={busy} tone="primary" onConfirm={() => void publish()}>
        {live ? 'The running version is replaced by this draft.' : 'The campaign starts sending.'} It reaches {reachText(confirm?.reach ?? null)}.
        {confirm && confirm.issues.some((i) => i.severity === 'warning') ? ` ${confirm.issues.filter((i) => i.severity === 'warning').length} warning(s) remain.` : ''}
      </ConfirmDialog>
    </section>
  );
}
